import setDynamicFormFieldValue from "@iewcasia/Store/Handlers/DynamicForm/SetFieldValue";
import { CustomState } from "@iewcasia/Store/Reducers";
import {
    DynamicFormOptionModel,
    getDynamicFormOptionDisplay,
    resolveDynamicFormOptions,
} from "@iewcasia/Utilities/dynamicFormOptions";
import { DynamicFormContext } from "@iewcasia/Widgets/DynamicForm/DynamicForm";
import DynamicFormFieldShell from "@iewcasia/Widgets/DynamicForm/DynamicFormFieldShell";
import { useRegisterDynamicFormFieldKeys } from "@iewcasia/Widgets/DynamicForm/useRegisterDynamicFormFieldKeys";
import siteMessage from "@insite/client-framework/SiteMessage";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Radio from "@insite/mobius/Radio";
import RadioGroup from "@insite/mobius/RadioGroup";
import React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    fieldKey = "fieldKey",
    label = "label",
    options = "options",
    isRequired = "isRequired",
    horizontal = "horizontal",
    defaultValue = "defaultValue",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.fieldKey]: string;
        [fields.label]: string;
        [fields.options]: DynamicFormOptionModel[];
        [fields.isRequired]: boolean;
        [fields.horizontal]: boolean;
        [fields.defaultValue]: string;
    };
}

/** Maps Dynamic Form Redux state into widget props. */
const mapStateToProps = (state: CustomState, ownProps: OwnProps) => {
    const fieldKey = ownProps.fields.fieldKey || "";
    return {
        fieldKey,
        fieldValue: state.customDynamicForm?.fieldValues[fieldKey],
    };
};

const mapDispatchToProps = {
    setDynamicFormFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const widgetGroup = "SalesForce Dynamic" as any;

/** Salesforce Dynamic Form radio field widget. */
const DynamicRadio: React.FC<Props> = ({
    id,
    fields: widgetFields,
    fieldKey,
    fieldValue,
    setDynamicFormFieldValue,
}) => {
    const { label, isRequired, options, horizontal, defaultValue } = widgetFields;
    const formContext = React.useContext(DynamicFormContext);
    const [errorMessage, setErrorMessage] = React.useState("");
    const resolvedOptions = React.useMemo(() => resolveDynamicFormOptions(options), [options]);
    useRegisterDynamicFormFieldKeys(id, fieldKey ? [fieldKey] : []);

    // Apply CMS default (e.g. marketing opt-in "true") once when the field is empty.
    React.useEffect(() => {
        if (!fieldKey || typeof fieldValue !== "undefined") {
            return;
        }
        const initial = (defaultValue || "").trim();
        if (!initial) {
            return;
        }
        const isValidDefault = resolvedOptions.some(option => option.value === initial);
        if (isValidDefault) {
            setDynamicFormFieldValue({ key: fieldKey, value: initial });
        }
    }, [fieldKey, fieldValue, defaultValue, resolvedOptions, setDynamicFormFieldValue]);

    const validate = React.useCallback(() => {
        const needsValue = isRequired && resolvedOptions.length > 0;
        const error = needsValue && !fieldValue ? (siteMessage("Field_Required", label) as string) : "";
        setErrorMessage(error);
        return !error;
    }, [fieldValue, isRequired, label, resolvedOptions.length]);

    React.useEffect(() => {
        if (!fieldKey) {
            return undefined;
        }
        formContext.validators[fieldKey] = validate;
        return () => {
            formContext.validators[fieldKey] = undefined;
        };
    }, [fieldKey, validate, formContext.validators]);

    React.useEffect(() => {
        if (errorMessage && fieldValue) {
            setErrorMessage("");
        }
    }, [fieldValue, errorMessage]);

    if (!fieldKey) {
        return null;
    }

    return (
        <DynamicFormFieldShell>
            <RadioGroup
                label={label}
                required={isRequired}
                value={fieldValue || ""}
                error={errorMessage || undefined}
                horizontal={!!horizontal}
                name={fieldKey}
                onChangeHandler={event =>
                    setDynamicFormFieldValue({ key: fieldKey, value: event.currentTarget.value })
                }
            >
                {resolvedOptions.map(option => (
                    <Radio key={option.value} value={option.value}>
                        {option.label}
                    </Radio>
                ))}
            </RadioGroup>
        </DynamicFormFieldShell>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(DynamicRadio),
    definition: {
        group: widgetGroup,
        displayName: "Radio",
        icon: "circle-dot",
        fieldDefinitions: [
            {
                name: fields.fieldKey,
                displayName: "Salesforce Field (API Name)",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.options,
                displayName: "Options",
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => getDynamicFormOptionDisplay(item),
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "label",
                        displayName: "Label",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "value",
                        displayName: "Value (sent to Salesforce)",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                        tooltip: "API value submitted to Salesforce (e.g. true / false or Yes / No).",
                    },
                ],
                defaultValue: [],
            },
            {
                name: fields.defaultValue,
                displayName: "Default Value",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                tooltip:
                    "Optional. Pre-selects the option whose Value matches (e.g. true for marketing opt-in Yes).",
            },
            {
                name: fields.horizontal,
                displayName: "Horizontal Layout",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
